import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck,
  Zap,
  Search,
  HeartPulse,
  CheckCircle2,
  Undo2,
} from "lucide-react";

const ROW_META = [
  {
    key: "primary_mitigation",
    icon: Zap,
    accent: "text-emerald-600",
    border: "border-emerald-500/30",
    bg: "bg-emerald-500/5",
  },
  {
    key: "secondary_mitigation",
    icon: ShieldCheck,
    accent: "text-sky-600",
    border: "border-sky-500/30",
    bg: "bg-sky-500/5",
  },
  {
    key: "diagnostic_validation",
    icon: Search,
    accent: "text-violet-600",
    border: "border-violet-500/30",
    bg: "bg-violet-500/5",
  },
  {
    key: "business_continuity",
    icon: HeartPulse,
    accent: "text-amber-600",
    border: "border-amber-500/30",
    bg: "bg-amber-500/5",
  },
  {
    key: "recovery_validation",
    icon: CheckCircle2,
    accent: "text-teal-600",
    border: "border-teal-500/30",
    bg: "bg-teal-500/5",
  },
  {
    key: "rollback_condition",
    icon: Undo2,
    accent: "text-rose-600",
    border: "border-rose-500/30",
    bg: "bg-rose-500/5",
  },
];

export default function FinalActionPlan({ plan }) {
  if (!plan) return null;
  return (
    <div
      className="cre-surface rounded-sm p-5 md:p-6"
      data-testid="final-action-plan"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="cre-overline text-emerald-600">
            Final recommendation
          </div>
          <h3 className="font-heading text-xl font-bold tracking-tight mt-1">
            Final Recommended Action Plan
          </h3>
          <p className="mt-1 text-xs text-slate-500 max-w-2xl">
            Deduplicated and safety-ordered recommendation chain — what to do
            first, what to fall back on, how to validate, how to keep the
            business running, how to confirm recovery, and when to roll back.
          </p>
        </div>
        <Badge
          variant="outline"
          className="rounded-sm font-mono text-[10px] uppercase tracking-widest border-slate-300 text-slate-500"
        >
          Advisory
        </Badge>
      </div>

      {plan.summary ? (
        <div
          data-testid="final-plan-summary"
          className="mt-3 text-xs font-mono text-slate-600"
        >
          {plan.summary}
        </div>
      ) : null}

      <ol className="mt-4 space-y-2.5">
        {ROW_META.map((meta, idx) => {
          const item = plan[meta.key];
          if (!item) return null;
          const Icon = meta.icon;
          return (
            <li
              key={meta.key}
              data-testid={`final-plan-${meta.key}`}
              className={`border ${meta.border} ${meta.bg} rounded-sm p-3.5 flex gap-3 items-start`}
            >
              <div className="flex flex-col items-center shrink-0 mt-0.5">
                <span className={`font-mono text-xs font-semibold ${meta.accent}`}>
                  {idx + 1}
                </span>
                <Icon
                  className={`h-4 w-4 mt-1 ${meta.accent}`}
                  strokeWidth={1.75}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`cre-overline ${meta.accent}`}>
                    {item.label}
                  </span>
                  {item.owner ? (
                    <span
                      data-testid={`final-plan-${meta.key}-owner`}
                      className="text-[10px] font-mono text-slate-500"
                    >
                      · owner: {item.owner}
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 text-sm text-slate-800 leading-snug break-words">
                  {item.action}
                </div>
                {item.detail ? (
                  <div className="mt-1 text-xs text-slate-500 leading-snug break-words">
                    {item.detail}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-4 text-[11px] font-mono text-slate-500">
        Generated deterministically from consensus, runbook, and guardrail
        outputs · Human approval required before execution.
      </div>
    </div>
  );
}
